/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { SEOConfig, SupportedLanguage } from './seo.types';
import { ENVIRONMENT } from '@shared/utils/environment';

const ENV_TITLE_PREFIX: Record<string, string> = {
  local: '[lcl]',
  testing: '[tst]',
};

const prefix = ENV_TITLE_PREFIX[ENVIRONMENT] || '';

/**
 * Default SEO configuration for the Housien application
 */
export const seoConfig: SEOConfig = {
  siteName: prefix ? `${prefix} Housien` : 'Housien',
  defaultDescription: '',
  siteUrl: 'https://www.housien.com.br',
  defaultImage: 'https://www.housien.com.br/images/logo/housien-light.png',
  defaultLanguage: 'pt-BR',
  supportedLanguages: ['en', 'pt-BR', 'es'],
  twitterHandle: '@housien',
  titleTemplate: prefix ? `${prefix} %s | Housien` : '%s | Housien',
};

/**
 * Locale mapping for Open Graph tags
 */
export const localeMap: Record<SupportedLanguage, string> = {
  'en': 'en_US',
  'pt-BR': 'pt_BR',
  'es': 'es_ES',
};

/**
 * Get the full title with site name
 * @param pageTitle - The page-specific title
 * @returns The formatted title with site name
 */
export function getFullTitle(pageTitle: string): string {
  if (!pageTitle || pageTitle === seoConfig.siteName) {
    return seoConfig.siteName;
  }
  return seoConfig.titleTemplate.replace('%s', pageTitle);
}

/**
 * Get the canonical URL for a path
 * @param path - The path to get the canonical URL for
 * @returns The full canonical URL
 */
export function getCanonicalUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${seoConfig.siteUrl}${cleanPath}`;
}

/**
 * Get the Open Graph locale for a language
 * @param language - The language code
 * @returns The Open Graph locale string
 */
export function getOGLocale(language: SupportedLanguage): string {
  return localeMap[language] || localeMap[seoConfig.defaultLanguage];
}

/**
 * Default meta tags that should be present on all pages
 */
// Note: themeColor must be a string value for meta tags, not a CSS variable
// This uses the seed-base color from the design system
export const defaultMetaTags = {
  viewport: 'width=device-width, initial-scale=1',
  themeColor: '#4a0e11', // Matches --seed-base from design-system.css
  robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
};

/**
 * Organization structured data for the site
 */
export const organizationData = {
  name: 'Housien',
  legalName: 'Knowledge for Decisions (K4D)',
  url: seoConfig.siteUrl,
  logo: seoConfig.defaultImage,
  description: seoConfig.defaultDescription,
  foundingDate: '2017',
  email: 'contact@k4d.com.br',
  address: {
    addressCountry: 'BR',
  },
  sameAs: [
    'https://www.linkedin.com/showcase/housien',
    'https://www.instagram.com/housien.br',
  ],
};
